import { Capacitor } from '@capacitor/core';
import { Geolocation, WatchPositionCallback } from '@capacitor/geolocation';
import { createStore } from '@stencil/store';
import { Position } from 'geojson';
import throttle from 'lodash.throttle';

export interface GeolocationStore {
  location: Position;
  accuracy: number;
  heading: number;
  /**
   * Last geolocation-related error message.
   * Set on permission failures, timeouts, or other location errors.
   * Cleared when a successful location is obtained.
   * Can be used by UI components to display error states to users.
   */
  error: string | null;
}

const store = createStore<GeolocationStore>({
  location: null,
  accuracy: null,
  heading: null,
  error: null
});

let callbackId: string = null;
let deviceOrientationHandler: ((ev: DeviceOrientationEvent) => void) | null = null;

// Geolocation retry state
let isRestarting = false;
let isInitializing = false;
let retryCount = 0;
let restartTimeoutId: number | null = null;
const BASE_RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRY_DELAY = 30000; // Cap at 30 seconds for responsive recovery when back online

// Smoothing state for heading
let smoothedHeading: number | null = null;
let lastRawHeading: number | null = null;
let lastProcessedTime: number = 0;

// Filtering constants
const SMOOTHING_FACTOR = 0.3; // Lower = more smoothing (0-1)
const MIN_HEADING_CHANGE = 2; // Minimum degrees change to update (reduces drift when still)
const MIN_RAW_CHANGE = 1; // Minimum raw change before even processing (early filter)
const MIN_TIME_BETWEEN_UPDATES = 50; // Minimum ms between processing events (reduces bridge overhead)

/**
 * Degrees to add to device-relative compass heading so the result is "north relative to screen top".
 * In portrait we use 0 (screen top = device top). In landscape we use 90 or 270 so the cone
 * points correctly; we detect portrait by dimensions so iPad (natural landscape) doesn't get
 * a wrong correction when held in portrait (API would report angle 90 there).
 */
const getScreenOrientationOffset = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }
  const isPortrait = window.innerWidth < window.innerHeight;
  if (isPortrait) {
    return 0;
  }
  const apiAngle =
    typeof screen !== 'undefined' && screen.orientation?.angle != null
      ? screen.orientation.angle
      : typeof (window as any).orientation === 'number'
        ? ((window as any).orientation < 0 ? (window as any).orientation + 360 : (window as any).orientation)
        : 0;
  // Landscape: screen top is 90° or 270° from device top. API gives 0/180 on iPad, 90/270 on phone.
  return apiAngle === 0 || apiAngle === 90 ? 90 : 270;
};

/**
 * Check if an error is a timeout error.
 * The Capacitor geolocation plugin kills the watch on timeout,
 * so we need to detect this and restart the watch.
 */
const isTimeoutError = (err: any): boolean => {
  if (!err) {
    return false;
  }
  // Check for the specific Capacitor error code
  if (err.code === 'OS-PLUG-GLOC-0010') {
    return true;
  }
  // Fallback: check message content
  if (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout')) {
    return true;
  }
  return false;
};

/**
 * Check if an error is a permanent/non-transient error that won't resolve on retry.
 * These include permission denied, location services disabled, etc.
 */
const isPermanentError = (err: any): boolean => {
  if (!err || !err.code) {
    return false;
  }
  // Permanent error codes from Capacitor Geolocation plugin
  const permanentErrorCodes = [
    'OS-PLUG-GLOC-0003', // Location permission request was denied
    'OS-PLUG-GLOC-0007', // Location services are not enabled
    'OS-PLUG-GLOC-0008', // Application's use of location services was restricted
    'OS-PLUG-GLOC-0009', // Request to enable location was denied (Android)
  ];
  return permanentErrorCodes.includes(err.code);
};

/**
 * Clear any pending restart timeout and reset restart state.
 * Called when starting a new watch or when we want to cancel pending restarts.
 */
const clearRestartTimeout = () => {
  if (restartTimeoutId !== null) {
    clearTimeout(restartTimeoutId);
    restartTimeoutId = null;
  }
  isRestarting = false;
};

/**
 * Schedule a restart of the geolocation watch with exponential backoff (capped at 30s).
 * This is called when a timeout error kills the watch.
 * Retries indefinitely to support offline field use - users may be without
 * signal for hours but need geolocation when they return to coverage.
 */
const scheduleWatchRestart = () => {
  if (isRestarting) {
    return;
  }

  isRestarting = true;
  // Exponential backoff capped at MAX_RETRY_DELAY for responsive recovery
  const delay = Math.min(BASE_RETRY_DELAY * Math.pow(2, retryCount), MAX_RETRY_DELAY);
  retryCount++;

  console.log(`Geolocation: scheduling restart in ${delay}ms (attempt ${retryCount})`);

  restartTimeoutId = window.setTimeout(async () => {
    restartTimeoutId = null;

    // Ensure any existing watch is properly cleared before starting a new one
    if (callbackId) {
      try {
        await Geolocation.clearWatch({ id: callbackId });
      } catch (clearError) {
        console.warn('Geolocation: failed to clear existing watch before restart', clearError);
      }
      callbackId = null;
    }

    let restartSucceeded = false;
    try {
      await geolocationStartTracking();
      restartSucceeded = true;
    } catch (error) {
      console.error('Geolocation: restart failed', error);
    } finally {
      // Allow new restart attempts to be scheduled
      isRestarting = false;
      // If this restart attempt failed, schedule another retry with backoff
      if (!restartSucceeded) {
        scheduleWatchRestart();
      }
    }
  }, delay);
};

const geolocationCallback: WatchPositionCallback = (position, err) => {
  if (err) {
    console.warn('Geolocation error:', err);

    // Check if this is a timeout error - the watch is now dead and needs restart
    if (isTimeoutError(err)) {
      store.state.error = 'Waiting for location...';

      // Clear the dead watch immediately to avoid keeping a stale reference
      if (callbackId) {
        // Best-effort clear; ignore errors from clearing a dead watch
        Geolocation.clearWatch({ id: callbackId }).catch(() => {});
        callbackId = null;
      }

      scheduleWatchRestart();
    } else {
      // Non-timeout error (e.g., permission denied, position unavailable) - surface to UI
      store.state.error = 'Unable to determine your location. Please check location permissions and settings.';
    }
    return;
  }

  // Success - reset retry state, cancel any pending restart, and clear any error
  retryCount = 0;
  clearRestartTimeout();
  store.state.error = null;
  store.state.location = [position.coords.longitude, position.coords.latitude];
  store.state.accuracy = position.coords.accuracy;
};

const smoothHeading = (newHeading: number): number | null => {
  // Normalize heading to 0-360
  const normalizedHeading = ((newHeading % 360) + 360) % 360;

  if (smoothedHeading === null) {
    // First reading - use it directly
    smoothedHeading = normalizedHeading;
    return normalizedHeading;
  }

  // Handle wraparound (e.g., 359° to 1° should be 2° change, not 358°)
  let diff = normalizedHeading - smoothedHeading;
  if (Math.abs(diff) > 180) {
    diff = diff > 0 ? diff - 360 : diff + 360;
  }

  // Only update if change is significant (reduces drift when device is still)
  if (Math.abs(diff) < MIN_HEADING_CHANGE) {
    return null; // Don't update for small changes
  }

  // Exponential smoothing
  smoothedHeading = smoothedHeading + diff * SMOOTHING_FACTOR;

  // Normalize result
  smoothedHeading = ((smoothedHeading % 360) + 360) % 360;

  return smoothedHeading;
};



const updateHeading = (heading: number) => {
  const smoothed = smoothHeading(heading);
  if (smoothed !== null) {
    store.state.heading = smoothed;
  }
};

const updateHeadingThrottled = throttle(updateHeading, 100);

// Ultra-lightweight listener wrapper: drops events immediately if too frequent
// Shared pipeline for heading updates (minimizes processing / throttling)
const handleCompassEvent = (event: { value: number }) => {
  const now = Date.now();

  // Layer 1: Time-based filter - drop events that arrive too quickly
  if (now - lastProcessedTime < MIN_TIME_BETWEEN_UPDATES) {
    return;
  }

  const newHeading = event.value;

  // Layer 2: First reading - always process
  if (lastRawHeading === null) {
    lastRawHeading = newHeading;
    lastProcessedTime = now;
    updateHeadingThrottled(newHeading);
    return;
  }

  // Layer 3: Calculate raw change (handle wraparound)
  let rawDiff = newHeading - lastRawHeading;
  if (Math.abs(rawDiff) > 180) {
    rawDiff = rawDiff > 0 ? rawDiff - 360 : rawDiff + 360;
  }

  // Layer 4: Change-based filter - only process if change is significant
  if (Math.abs(rawDiff) >= MIN_RAW_CHANGE) {
    lastRawHeading = newHeading;
    lastProcessedTime = now;
    updateHeadingThrottled(newHeading);
  }
};

/** Adapt DeviceOrientationEvent into the shared heading pipeline. */
const handleDeviceOrientationEvent = (ev: DeviceOrientationEvent) => {
  const rawHeading =
    'webkitCompassHeading' in ev && typeof (ev as any).webkitCompassHeading === 'number'
      ? (ev as any).webkitCompassHeading
      : ev.alpha != null
        ? (360 - ev.alpha) % 360
        : null;
  if (rawHeading != null) {
    const offset = getScreenOrientationOffset();
    const heading = (rawHeading + offset) % 360;
    handleCompassEvent({ value: heading });
  }
};

/** Start compass using web DeviceOrientation API (both platforms; on iOS requires user gesture for requestPermission). */
const startWebCompass = async (): Promise<boolean> => {
  const eventName =
    'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
  const requestPermission =
    typeof (DeviceOrientationEvent as any).requestPermission === 'function'
      ? (DeviceOrientationEvent as any).requestPermission
      : null;

  if (requestPermission) {
    try {
      const result = await requestPermission();
      if (result !== 'granted') {
        return false;
      }
    } catch {
      return false;
    }
  }

  deviceOrientationHandler = handleDeviceOrientationEvent;
  window.addEventListener(eventName, deviceOrientationHandler, { passive: true });
  return true;
};

export const startDeviceOrientation = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  if (deviceOrientationHandler !== null) {
    return;
  }

  smoothedHeading = null;
  lastRawHeading = null;
  lastProcessedTime = 0;

  await startWebCompass();
  // If startWebCompass returns false (e.g. permission denied on iOS), we simply don't show heading.
};

export const stopDeviceOrientation = async () => {
  try {
    if (deviceOrientationHandler) {
      const eventName =
        'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
      window.removeEventListener(eventName, deviceOrientationHandler);
      deviceOrientationHandler = null;
    }
    smoothedHeading = null;
    lastRawHeading = null;
    lastProcessedTime = 0;
  } catch (err) {
    console.error('Error stopping compass:', err);
  }
};

export const geolocationStartTracking = async (): Promise<boolean> => {
  // Already tracking
  if (callbackId) {
    return true;
  }

  // Prevent concurrent initialization attempts
  if (isInitializing) {
    return false;
  }

  isInitializing = true;

  // Clear any pending restart timeout since we're starting fresh
  clearRestartTimeout();

  try {
    callbackId = await Geolocation.watchPosition({
      enableHighAccuracy: true,
      enableLocationFallback: true,
      timeout: 20000,
      interval: 2000,
      minimumUpdateInterval: 2000,
      maximumAge: 0
    }, geolocationCallback);
    // Successfully started - reset retry count for fresh backoff on future errors
    retryCount = 0;
    return true;
  } catch (error) {
    console.error('Geolocation: failed to start tracking', error);
    store.state.error = 'Failed to start location tracking';

    // Only schedule a retry for transient errors, not permanent ones like permission denied
    if (!isPermanentError(error)) {
      scheduleWatchRestart();
    }
    return false;
  } finally {
    isInitializing = false;
  }
}

export const state = store.state;
export const onChange = store.onChange;
export default state;
